import { useCallback, useContext, useEffect, useState } from "react";

import Sidebar from "../../shared/components/sidebar/Sidebar";
import Navbar from "../../shared/components/navbar/Navbar";
import { AuthContext } from "../../shared/context/auth-context";
import { ClinicaContext } from "../../shared/context/clinica-context";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { useLoading } from "../../shared/context/LoadingContext";
import toast from "react-hot-toast";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BadgeUnidade from "../../shared/components/badgeUnidade/BadgeUnidade";

import "./PedidosMarcacao.scss";

/**
 * Os pedidos que chegaram da página pública de marcações.
 *
 * ─── UM PEDIDO NÃO É UMA MARCAÇÃO ───────────────────────────────────────────
 *
 * Nada aqui ocupa horário. Vários pedidos podem coincidir, e enquanto estão
 * nesta lista a agenda continua a poder ser preenchida normalmente por quem
 * atende — e deve. É a aprovação que decide e que cria a marcação.
 *
 * Daí a ordem das colunas: o horário pedido primeiro, porque é o que diz se
 * ainda vale a pena ligar à pessoa.
 */
const PedidosMarcacao = () => {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [pedidos, setPedidos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tratamentos, setTratamentos] = useState([]);
  const [aDecidir, setADecidir] = useState(null);

  const clinicaId = clinica.clinica._id;

  const carregar = useCallback(async () => {
    try {
      startLoading();
      const [p, c, t] = await Promise.all([
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/pedidosmarcacao/pendentes/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/nomeidmoradacontactonif/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/clinica/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        ),
      ]);

      setPedidos(p.pedidos || []);
      setClientes(c.clientes || []);
      setTratamentos(t.tratamentos || []);
    } catch (err) {
      console.error("Erro ao carregar pedidos:", err);
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token, clinicaId, sendRequest]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const decidido = () => {
    setADecidir(null);
    carregar();
  };

  return (
    <div className="list">
      <Sidebar />
      <div className="listContainer">
        <Navbar title="Pedidos de marcação" icon={CalendarMonthIcon} />

        <div className="pedidos-marcacao caixa">
          <p className="pedidos-marcacao__nota">
            Estes pedidos vieram da página pública e <strong>não ocupam
            horário nenhum</strong>. A marcação só existe depois de aprovar.
          </p>

          {pedidos.length === 0 ? (
            <p className="pedidos-marcacao__vazio">Não há pedidos pendentes.</p>
          ) : (
            <ul className="pedidos-marcacao__lista">
              {pedidos.map((p) => (
                <Pedido
                  key={p._id}
                  pedido={p}
                  aberto={aDecidir === p._id}
                  aoAbrir={() => setADecidir(p._id)}
                  aoFechar={() => setADecidir(null)}
                  clientes={clientes}
                  tratamentos={tratamentos}
                  auth={auth}
                  sendRequest={sendRequest}
                  aoDecidir={decidido}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const dataLegivel = (iso) =>
  new Date(iso).toLocaleString("pt-PT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Dígitos só, para comparar telemóveis escritos de maneiras diferentes. */
const digitos = (v) => String(v || "").replace(/\D/g, "");

function Pedido({
  pedido,
  aberto,
  aoAbrir,
  aoFechar,
  clientes,
  tratamentos,
  auth,
  sendRequest,
  aoDecidir,
}) {
  const [tipo, setTipo] = useState("AVALIACAO");
  const [clienteId, setClienteId] = useState("");
  const [criarCliente, setCriarCliente] = useState(false);
  const [procura, setProcura] = useState("");
  const [medicoId, setMedicoId] = useState("");
  const [tratamento, setTratamento] = useState("");
  const [motivo, setMotivo] = useState("");
  const [aEnviar, setAEnviar] = useState(false);

  /* Quem já é paciente, pelo telemóvel. É o que evita criar a segunda ficha da
     mesma pessoa — e é por isso que a sugestão aparece em destaque em vez de
     se perder numa lista de mil nomes. */
  const sugestoes = clientes.filter(
    (c) => digitos(c.contacto) && digitos(c.contacto) === digitos(pedido.telemovel),
  );

  /* Autocomplete e não uma lista: uma clínica tem milhares de fichas, e um
     `select` com todas é impossível de usar e pesado de desenhar. Procura por
     nome ou por contacto — quem atende tanto tem o nome como o número.

     O teto de 8 é para a lista não tapar o ecrã; quem não encontrar nas 8
     primeiras escreve mais duas letras. */
  const termo = procura.trim().toLowerCase();
  const encontrados = termo
    ? clientes
        .filter(
          (c) =>
            String(c.nome).toLowerCase().includes(termo) ||
            digitos(c.contacto).includes(digitos(termo)),
        )
        .slice(0, 8)
    : [];

  const escolhido = clientes.find((c) => c.id === clienteId);

  const medicos =
    auth.role === "Dentista" || auth.role === "Médico/Administrador"
      ? [{ id: auth.userId, nome: auth.nome }]
      : auth.usuariosAssociados || [];

  const aprovar = async () => {
    setAEnviar(true);
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/pedidosmarcacao/${pedido._id}/aprovar`,
        "PATCH",
        JSON.stringify({
          clienteId: criarCliente ? undefined : clienteId,
          criarCliente,
          medicoId,
          utilizador: auth.userId,
          tipo,
          tratamento: tipo === "ISOLADO" ? tratamento : undefined,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      toast.success(
        criarCliente
          ? "Aprovado. Ficha criada com nome e contacto — complete-a quando o cliente chegar."
          : "Pedido aprovado. A marcação foi criada.",
      );
      aoDecidir();
    } catch (err) {
      /* O 409 é a mensagem que interessa — «já foi decidido» ou «o horário já
         não está livre». Vem do servidor pronta a mostrar. */
      toast.error(err.message || "Não foi possível aprovar.");
    } finally {
      setAEnviar(false);
    }
  };

  const recusar = async () => {
    setAEnviar(true);
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/pedidosmarcacao/${pedido._id}/recusar`,
        "PATCH",
        JSON.stringify({ utilizador: auth.userId, motivo }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      toast.success("Pedido recusado.");
      aoDecidir();
    } catch (err) {
      toast.error(err.message || "Não foi possível recusar.");
    } finally {
      setAEnviar(false);
    }
  };

  /* Um pedido de urgencia nao se aprova: nao traz horario, logo nao ha
     marcacao nenhuma para criar. Resolve-se ao telefone, e fecha-se aqui. */
  const marcarTratado = async () => {
    setAEnviar(true);
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/pedidosmarcacao/${pedido._id}/tratado`,
        "PATCH",
        JSON.stringify({ utilizador: auth.userId, nota: motivo }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      toast.success("Pedido fechado.");
      aoDecidir();
    } catch (err) {
      toast.error(err.message || "Nao foi possivel fechar o pedido.");
    } finally {
      setAEnviar(false);
    }
  };

  /* Sem horario = veio da urgencia. */
  const semHorario = !pedido.inicio;

  const podeAprovar =
    (criarCliente || clienteId) &&
    medicoId &&
    (tipo !== "ISOLADO" || tratamento);

  return (
    <li className="pedidos-marcacao__item">
      <div className="pedidos-marcacao__cabecalho">
        <div>
          {/* Um pedido de urgência não traz horário: a pessoa descreveu o que
              tem e deixou o contacto, à espera que a clínica ligue. Mostrar
              uma data vazia dava a entender que algo falhou. */}
          <span className="pedidos-marcacao__hora">
            {pedido.inicio ? dataLegivel(pedido.inicio) : "Sem horário — a aguardar contacto"}
          </span>
          <BadgeUnidade estabelecimento={pedido.estabelecimento} />
        </div>
        <span className="pedidos-marcacao__recebido">
          pedido em {new Date(pedido.created_at).toLocaleDateString("pt-PT")}
        </span>
      </div>

      <div className="pedidos-marcacao__pessoa">
        <strong>{pedido.nome}</strong>
        <span>{pedido.telemovel}</span>
        {pedido.email && <span>{pedido.email}</span>}
      </div>

      {/* Ao que a pessoa vem. É a primeira coisa que quem decide quer saber —
          por isso vem antes das observações e antes do botão. */}
      {pedido.motivoEtiqueta && (
        <p
          className={`pedidos-marcacao__motivo${
            pedido.prioritario ? " pedidos-marcacao__motivo--urgente" : ""
          }`}
        >
          {/* O selo explica porque este pedido está no topo. A lista ordena os
              prioritários primeiro, e uma ordem sem explicação parece avaria. */}
          {pedido.prioritario && (
            <span className="pedidos-marcacao__selo">Contactar com prioridade</span>
          )}
          <strong>{pedido.motivoEtiqueta}</strong>
          {pedido.motivoDescricao && <span> — {pedido.motivoDescricao}</span>}
        </p>
      )}

      {pedido.observacoes && (
        <p className="pedidos-marcacao__observacoes">{pedido.observacoes}</p>
      )}

      {!aberto ? (
        <button className="pedidos-marcacao__btn" onClick={aoAbrir}>
          {semHorario ? "Contactar" : "Decidir"}
        </button>
      ) : semHorario ? (
        /* ─── Urgencia: so ha uma coisa a fazer ──────────────────────────
           Ligar ao numero que esta no cartao e, depois, fechar. O que for
           combinado ao telefone faz-se pelos caminhos normais do sistema —
           aqui nao se cria marcacao nenhuma. */
        <div className="pedidos-marcacao__decisao">
          <p className="pedidos-marcacao__aviso">
            Ligue para <strong>{pedido.telemovel}</strong> e combine com a
            pessoa. Depois feche este pedido.
          </p>
          <label className="pedidos-marcacao__etiqueta" htmlFor={`nota-${pedido._id}`}>
            Nota (opcional)
          </label>
          <input
            id={`nota-${pedido._id}`}
            className="pedidos-marcacao__input"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: marcada avaliacao para 23/10"
            maxLength={300}
          />
          <div className="pedidos-marcacao__acoes">
            <button
              className="pedidos-marcacao__btn pedidos-marcacao__btn--secundario"
              onClick={aoFechar}
              disabled={aEnviar}
            >
              Cancelar
            </button>
            <button
              className="pedidos-marcacao__btn"
              onClick={marcarTratado}
              disabled={aEnviar}
            >
              Fechar pedido
            </button>
          </div>
        </div>
      ) : (
        <div className="pedidos-marcacao__decisao">
          <div className="pedidos-marcacao__cliente">
            <span className="pedidos-marcacao__etiqueta">Cliente</span>

            {/* Quem tem o mesmo telemóvel aparece primeiro e sem ter de
                procurar: é o que evita a segunda ficha da mesma pessoa. */}
            {sugestoes.length > 0 && !criarCliente && (
              <div className="pedidos-marcacao__sugestoes">
                <span>Já existe com este telemóvel:</span>
                {sugestoes.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={
                      "pedidos-marcacao__sugestao" +
                      (clienteId === c.id
                        ? " pedidos-marcacao__sugestao--escolhida"
                        : "")
                    }
                    onClick={() => {
                      setClienteId(c.id);
                      setProcura("");
                    }}
                  >
                    {c.nome}
                  </button>
                ))}
              </div>
            )}

            {!criarCliente && (
              <>
                {escolhido ? (
                  <div className="pedidos-marcacao__escolhido">
                    <strong>{escolhido.nome}</strong>
                    <button
                      type="button"
                      onClick={() => {
                        setClienteId("");
                        setProcura("");
                      }}
                    >
                      trocar
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Procurar por nome ou contacto…"
                      value={procura}
                      onChange={(e) => setProcura(e.target.value)}
                    />
                    {termo && encontrados.length === 0 && (
                      <p className="pedidos-marcacao__aviso">
                        Nenhum cliente encontrado.
                      </p>
                    )}
                    {encontrados.length > 0 && (
                      <ul className="pedidos-marcacao__resultados">
                        {encontrados.map((c) => (
                          <li key={c.id}>
                            <button
                              type="button"
                              onClick={() => {
                                setClienteId(c.id);
                                setProcura("");
                              }}
                            >
                              <strong>{c.nome}</strong>
                              {c.contacto && <span>{c.contacto}</span>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </>
            )}

            {/* A ficha nova leva só o nome e o contacto do pedido. O resto
                completa-se quando a pessoa aparecer ao balcão. */}
            <label className="pedidos-marcacao__novo">
              <input
                type="checkbox"
                checked={criarCliente}
                onChange={(e) => {
                  setCriarCliente(e.target.checked);
                  if (e.target.checked) {
                    setClienteId("");
                    setProcura("");
                  }
                }}
              />
              <span>
                É cliente novo — criar ficha com <strong>{pedido.nome}</strong>{" "}
                e <strong>{pedido.telemovel}</strong>
              </span>
            </label>

            {criarCliente && sugestoes.length > 0 && (
              <p className="pedidos-marcacao__aviso">
                ⚠️ Já existe uma ficha com este telemóvel. Confirme que não é a
                mesma pessoa antes de criar outra.
              </p>
            )}
          </div>

          <label>
            <span>Médico</span>
            <select
              value={medicoId}
              onChange={(e) => setMedicoId(e.target.value)}
            >
              <option value="">Escolher…</option>
              {medicos.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Tipo</span>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="AVALIACAO">Avaliação</option>
              <option value="ISOLADO">Tratamento isolado</option>
            </select>
          </label>

          {tipo === "ISOLADO" && (
            <label>
              <span>Tratamento</span>
              <select
                value={tratamento}
                onChange={(e) => setTratamento(e.target.value)}
              >
                <option value="">Escolher…</option>
                {tratamentos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.designacao}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="pedidos-marcacao__acoes">
            <button
              className="pedidos-marcacao__btn pedidos-marcacao__btn--aprovar"
              onClick={aprovar}
              disabled={!podeAprovar || aEnviar}
            >
              Aprovar
            </button>
            <button
              className="pedidos-marcacao__btn pedidos-marcacao__btn--recusar"
              onClick={recusar}
              disabled={aEnviar}
            >
              Recusar
            </button>
            <button className="pedidos-marcacao__btn" onClick={aoFechar}>
              Cancelar
            </button>
          </div>

          <label>
            <span>Motivo da recusa (interno, opcional)</span>
            <input
              type="text"
              maxLength={300}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
        </div>
      )}
    </li>
  );
}

export default PedidosMarcacao;
